import { useCallback, useMemo } from 'react';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/shim/with-selector';

import { createDataSource } from '@adv-frontend/reactions';

import type { GetStoreFn, DropFirstParameter, StoreSubscribeListener, AnySelector } from '../types';

import { addCurrentSelectorStore, createSubscribableSelector } from './createSubscribableSelector';
import { triggerSelectorCall } from './trackSelectorCall';
import { subscribeToSelector } from './subscribeToSelector';

/**
 * "Привязанные" к стору геттер и хук, созданные из селектора
 *
 * @internal
 */
export type BoundSelectorAndHook<Selector extends AnySelector> = [
    DropFirstParameter<Selector>,
    DropFirstParameter<Selector>,
];

let isRootSelector = true;

/**
 * Функция для создания "привязанных" к стору геттера и хука из селектора
 *
 * @internal
 */
export function getBoundSelectorAndHook<Selector extends AnySelector>({
    getSelector,
    getStore,
}: {
    /**
     * Функция для получения селектора
     * Передаем селектор как функцию для того,
     * чтобы была возможность его подменить после инициализации приложения
     */
    getSelector: () => Selector;
    /** Функция для получения стора */
    getStore: GetStoreFn;
}): BoundSelectorAndHook<Selector> {
    type SelectorState = Parameters<Selector>[0];
    type SelectorArgsWithoutState = Parameters<DropFirstParameter<Selector>>;

    const boundSelector = (...selectorArgs: SelectorArgsWithoutState) => {
        const selector = getSelector();
        const store = getStore();

        /**
         * Сообщает о вызове модульного селектора, для проверки использования во внешних сторах
         */
        triggerSelectorCall();

        /**
         * При выполнении селектора сохраняем его стор в общий список сторов для текущего вызова.
         * Позднее они могут быть использованы для построения функции подписки на все задействованные сторы.
         */
        addCurrentSelectorStore(store);

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        return selector(store.getState() as SelectorState, ...selectorArgs);
    };

    const selectorDataSource: (...selectorArgs: Parameters<DropFirstParameter<Selector>>) => unknown = createDataSource(
        {
            createSubscribe(_, ...args) {
                // Создаем подписки только на селекторы верхнего уровня (игнорируем вложенные)
                if (!isRootSelector) {
                    return undefined;
                }

                return function selectorSubscription(notify) {
                    // subscribeToSelector вызывает селектор и нотификацию сразу же. Поэтому игнорируем первую нотификацию
                    let isFirstNotify = true;

                    return subscribeToSelector(
                        () => {
                            try {
                                return boundSelector(...args);
                            } catch (e) {
                                console.error(e);
                                // Возвращаем ошибку (а не undefined) чтобы селектор пересчитался, если boundSelector возвращает undefined после возникновения ошибки
                                return e;
                            }
                        },
                        () => {
                            if (isFirstNotify) {
                                isFirstNotify = false;
                            } else {
                                notify();
                            }
                        }
                    );
                };
            },
            getValueInReaction(...selectorArgs) {
                if (isRootSelector) {
                    try {
                        isRootSelector = false;
                        return boundSelector(...selectorArgs);
                    } finally {
                        isRootSelector = true;
                    }
                }
                return boundSelector(...selectorArgs);
            },
            getValue: boundSelector,
        }
    );

    const useBoundSelector = (...selectorArgs: SelectorArgsWithoutState) => {
        const store = getStore();
        const selector = useMemo(() => {
            const { isStoresChangedSinceLastSelect, select, subscribeToSelector, subscribeToStores } =
                createSubscribableSelector(getSelector(), {
                    // От изменений в текущем сторе не нужно никогда отписываться, кроме unmount компонента.
                    preserveSubscriptionFor: store,
                });

            const getSnapshot = () => {
                /**
                 * Если селектор нужно пересчитать из-за изменений в другом сторе, то нужно принудительно поменять
                 * значение снепшота, потому что стейт в текущем сторе не изменился.
                 */
                if (isStoresChangedSinceLastSelect()) {
                    return { ...store.getState() };
                }

                return store.getState();
            };

            return {
                select,
                getSnapshot,
                getServerSnapshot: getSnapshot,
                subscribe(listener: StoreSubscribeListener) {
                    const unsubscribeStores = subscribeToStores();
                    const unsubscribeSelector = subscribeToSelector(listener);

                    return () => {
                        unsubscribeStores();
                        unsubscribeSelector();
                    };
                },
            };
        }, [store]);

        const boundSelector = useCallback(
            (state: SelectorState) => selector.select(state, ...selectorArgs),
            // eslint-disable-next-line react-hooks/exhaustive-deps -- DIRECT-239681
            [selector.select, ...selectorArgs]
        );

        return useSyncExternalStoreWithSelector(
            selector.subscribe,
            selector.getSnapshot,
            selector.getServerSnapshot,
            boundSelector
        );
    };

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    return [useBoundSelector, selectorDataSource] as BoundSelectorAndHook<Selector>;
}
